/**
 * Calendar Data Import/Export Utilities for MyClass App
 * Handles exporting and importing calendar data in JSON format
 */

import { supabaseAdmin } from './supabase';

/**
 * Export Calendar_Event data for a specific academic year
 * @param {string} academicYear - Academic year (e.g., "2024-2025")
 * @returns {Promise<Object>} Export data object
 */
export const exportCalendarEvents = async (academicYear) => {
  try {
    // Get academic year ID
    const { data: academicYearData } = await supabaseAdmin
      .from('academic_years')
      .select('*')
      .eq('year_code', academicYear)
      .single();

    if (!academicYearData) {
      throw new Error(`Academic year ${academicYear} not found`);
    }

    // Get school events
    const { data: schoolEvents, error: eventsError } = await supabaseAdmin
      .from('school_events')
      .select('*')
      .eq('academic_year_id', academicYearData.id)
      .order('event_date', { ascending: true });

    if (eventsError) throw eventsError;

    // Get cycle day configuration
    const { data: cycleConfig, error: cycleError } = await supabaseAdmin
      .from('cycle_days_config')
      .select('*')
      .eq('academic_year_id', academicYearData.id)
      .single();

    if (cycleError) throw cycleError;

    // Create export object
    const exportData = {
      exportType: 'Calendar_Event',
      academicYear: academicYear,
      exportDate: new Date().toISOString(),
      version: '1.0',
      data: {
        academicYear: {
          year_code: academicYearData.year_code,
          start_date: academicYearData.start_date,
          end_date: academicYearData.end_date,
          description: academicYearData.description
        },
        schoolEvents: schoolEvents || [],
        cycleConfig: cycleConfig || null
      }
    };

    return exportData;
  } catch (error) {
    console.error('Error exporting calendar events:', error);
    throw error;
  }
};

/**
 * Export Calendar_ECA data for a specific academic year
 * @param {string} academicYear - Academic year (e.g., "2024-2025")
 * @returns {Promise<Object>} Export data object
 */
export const exportCalendarECA = async (academicYear) => {
  try {
    // Get academic year ID
    const { data: academicYearData } = await supabaseAdmin
      .from('academic_years')
      .select('*')
      .eq('year_code', academicYear)
      .single();

    if (!academicYearData) {
      throw new Error(`Academic year ${academicYear} not found`);
    }

    // Get ECA events with related data
    const { data: ecaEvents, error: eventsError } = await supabaseAdmin
      .from('eca_events')
      .select(`
        *,
        eca_activities (
          name,
          name_en,
          category_id,
          description,
          eca_categories (name, name_en, color_code, description)
        )
      `)
      .eq('academic_year_id', academicYearData.id)
      .order('event_date', { ascending: true });

    if (eventsError) throw eventsError;

    // Get ECA activities
    const { data: ecaActivities, error: activitiesError } = await supabaseAdmin
      .from('eca_activities')
      .select('*')
      .eq('academic_year_id', academicYearData.id);

    if (activitiesError) throw activitiesError;

    // Get ECA categories
    const { data: ecaCategories, error: categoriesError } = await supabaseAdmin
      .from('eca_categories')
      .select('*');

    if (categoriesError) throw categoriesError;

    // Create export object
    const exportData = {
      exportType: 'Calendar_ECA',
      academicYear: academicYear,
      exportDate: new Date().toISOString(),
      version: '1.0',
      data: {
        academicYear: {
          year_code: academicYearData.year_code,
          start_date: academicYearData.start_date,
          end_date: academicYearData.end_date,
          description: academicYearData.description
        },
        ecaEvents: ecaEvents || [],
        ecaActivities: ecaActivities || [],
        ecaCategories: ecaCategories || []
      }
    };

    return exportData;
  } catch (error) {
    console.error('Error exporting calendar ECA:', error);
    throw error;
  }
};

/**
 * Import Calendar_Event data to Supabase
 * @param {Object} importData - Import data object
 * @param {boolean} overwrite - Whether to overwrite existing data
 * @param {boolean} permanentSave - Whether to permanently save to database (false = view-only)
 * @returns {Promise<Object>} Import result
 */
export const importCalendarEvents = async (importData, overwrite = false, permanentSave = false) => {
  try {
    // Validate import data
    if (importData.exportType !== 'Calendar_Event') {
      throw new Error('Invalid export type. Expected Calendar_Event data.');
    }

    const { data: importResult } = importData;
    const academicYear = importData.academicYear;

    // Check if academic year exists, create if not
    let { data: academicYearData } = await supabaseAdmin
      .from('academic_years')
      .select('*')
      .eq('year_code', academicYear)
      .single();

    if (!academicYearData) {
      const { data: newYear, error: yearError } = await supabaseAdmin
        .from('academic_years')
        .insert([importResult.academicYear])
        .select()
        .single();

      if (yearError) throw yearError;
      academicYearData = newYear;
    }

    // If not permanent save, just return preview data
    if (!permanentSave) {
      return {
        success: true,
        message: `Preview mode: ${importResult.schoolEvents.length} school events and cycle configuration ready for ${academicYear}`,
        importedEvents: importResult.schoolEvents.length,
        cycleConfig: importResult.cycleConfig ? 'ready' : 'none',
        previewMode: true,
        data: {
          schoolEvents: importResult.schoolEvents,
          cycleConfig: importResult.cycleConfig
        }
      };
    }

    // Handle overwrite logic for permanent save
    if (overwrite) {
      // Delete existing data
      await supabaseAdmin
        .from('school_events')
        .delete()
        .eq('academic_year_id', academicYearData.id);

      await supabaseAdmin
        .from('cycle_days_config')
        .delete()
        .eq('academic_year_id', academicYearData.id);
    }

    // Import school events - properly clean the data
    const schoolEventsToInsert = importResult.schoolEvents.map(event => {
      const cleanEvent = { ...event };
      // Remove problematic fields that might cause constraint violations
      delete cleanEvent.id;
      delete cleanEvent.created_at;
      delete cleanEvent.updated_at;
      
      return {
        ...cleanEvent,
        academic_year_id: academicYearData.id
      };
    });

    const { data: importedEvents, error: eventsError } = await supabaseAdmin
      .from('school_events')
      .insert(schoolEventsToInsert)
      .select();

    if (eventsError) throw eventsError;

    // Import cycle configuration - properly clean the data
    let cycleConfigResult = null;
    if (importResult.cycleConfig) {
      const cleanCycleConfig = { ...importResult.cycleConfig };
      delete cleanCycleConfig.id;
      delete cleanCycleConfig.created_at;
      delete cleanCycleConfig.updated_at;

      const cycleConfigToInsert = {
        ...cleanCycleConfig,
        academic_year_id: academicYearData.id
      };

      const { data: importedCycleConfig, error: cycleError } = await supabaseAdmin
        .from('cycle_days_config')
        .insert([cycleConfigToInsert])
        .select()
        .single();

      if (cycleError) throw cycleError;
      cycleConfigResult = importedCycleConfig;
    }

    return {
      success: true,
      message: `Successfully imported ${importedEvents.length} school events and cycle configuration for ${academicYear}`,
      importedEvents: importedEvents.length,
      cycleConfig: cycleConfigResult ? 'imported' : 'none',
      previewMode: false
    };

  } catch (error) {
    console.error('Error importing calendar events:', error);
    throw error;
  }
};

/**
 * Import Calendar_ECA data to Supabase
 * @param {Object} importData - Import data object
 * @param {boolean} overwrite - Whether to overwrite existing data
 * @param {boolean} permanentSave - Whether to permanently save to database (false = view-only)
 * @returns {Promise<Object>} Import result
 */
export const importCalendarECA = async (importData, overwrite = false, permanentSave = false) => {
  try {
    // Validate import data
    if (importData.exportType !== 'Calendar_ECA') {
      throw new Error('Invalid export type. Expected Calendar_ECA data.');
    }

    const { data: importResult } = importData;
    const academicYear = importData.academicYear;

    // Check if academic year exists, create if not
    let { data: academicYearData } = await supabaseAdmin
      .from('academic_years')
      .select('*')
      .eq('year_code', academicYear)
      .single();

    if (!academicYearData) {
      const { data: newYear, error: yearError } = await supabaseAdmin
        .from('academic_years')
        .insert([importResult.academicYear])
        .select()
        .single();

      if (yearError) throw yearError;
      academicYearData = newYear;
    }

    // If not permanent save, just return preview data
    if (!permanentSave) {
      return {
        success: true,
        message: `Preview mode: ${importResult.ecaEvents.length} ECA events, ${importResult.ecaActivities.length} activities, and ${importResult.ecaCategories.length} categories ready for ${academicYear}`,
        importedEvents: importResult.ecaEvents.length,
        importedActivities: importResult.ecaActivities.length,
        importedCategories: importResult.ecaCategories.length,
        previewMode: true,
        data: {
          ecaEvents: importResult.ecaEvents,
          ecaActivities: importResult.ecaActivities,
          ecaCategories: importResult.ecaCategories
        }
      };
    }

    // Handle overwrite logic for permanent save
    if (overwrite) {
      // Delete existing data
      await supabaseAdmin
        .from('eca_events')
        .delete()
        .eq('academic_year_id', academicYearData.id);

      await supabaseAdmin
        .from('eca_activities')
        .delete()
        .eq('academic_year_id', academicYearData.id);
    }

    // Import ECA categories first (if they don't exist)
    const existingCategories = await supabaseAdmin
      .from('eca_categories')
      .select('*');

    const categoriesToInsert = importResult.ecaCategories.filter(category => 
      !existingCategories.data.some(existing => existing.name === category.name)
    );

    let categoryMap = {};
    if (categoriesToInsert.length > 0) {
      const { data: importedCategories, error: categoriesError } = await supabaseAdmin
        .from('eca_categories')
        .insert(categoriesToInsert)
        .select();

      if (categoriesError) throw categoriesError;

      // Create mapping for category IDs
      importedCategories.forEach(category => {
        categoryMap[category.name] = category.id;
      });
    }

    // Map existing categories
    existingCategories.data.forEach(category => {
      categoryMap[category.name] = category.id;
    });

    // Import ECA activities - properly clean the data
    const activitiesToInsert = importResult.ecaActivities.map(activity => {
      const cleanActivity = { ...activity };
      delete cleanActivity.id;
      delete cleanActivity.created_at;
      delete cleanActivity.updated_at;
      
      return {
        ...cleanActivity,
        academic_year_id: academicYearData.id,
        category_id: categoryMap[activity.eca_categories?.name] || activity.category_id
      };
    });

    const { data: importedActivities, error: activitiesError } = await supabaseAdmin
      .from('eca_activities')
      .insert(activitiesToInsert)
      .select();

    if (activitiesError) throw activitiesError;

    // Create activity ID mapping
    const activityMap = {};
    importedActivities.forEach(activity => {
      const originalActivity = importResult.ecaActivities.find(a => 
        a.name === activity.name && a.academic_year_id === activity.academic_year_id
      );
      if (originalActivity) {
        activityMap[originalActivity.id] = activity.id;
      }
    });

    // Import ECA events - properly clean the data
    const eventsToInsert = importResult.ecaEvents.map(event => {
      const cleanEvent = { ...event };
      delete cleanEvent.id;
      delete cleanEvent.created_at;
      delete cleanEvent.updated_at;
      
      return {
        ...cleanEvent,
        academic_year_id: academicYearData.id,
        activity_id: activityMap[event.activity_id] || event.activity_id
      };
    });

    const { data: importedEvents, error: eventsError } = await supabaseAdmin
      .from('eca_events')
      .insert(eventsToInsert)
      .select();

    if (eventsError) throw eventsError;

    return {
      success: true,
      message: `Successfully imported ${importedEvents.length} ECA events, ${importedActivities.length} activities, and ${categoriesToInsert.length} new categories for ${academicYear}`,
      importedEvents: importedEvents.length,
      importedActivities: importedActivities.length,
      importedCategories: categoriesToInsert.length,
      previewMode: false
    };

  } catch (error) {
    console.error('Error importing calendar ECA:', error);
    throw error;
  }
};

/**
 * Download data as JSON file
 * @param {Object} data - Data to export
 * @param {string} filename - Filename for download
 */
export const downloadJSON = (data, filename) => {
  const dataStr = JSON.stringify(data, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Read JSON file from file input
 * @param {File} file - File object from input
 * @returns {Promise<Object>} Parsed JSON data
 */
export const readJSONFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        resolve(data);
      } catch (error) {
        reject(new Error('Invalid JSON file'));
      }
    };
    
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
};
